import type {AppPaginatedList} from "../../../../types/pagination";
import type {MutationCallbacks} from "../../types/callbacks";
import type {User} from "@project/sdk/admin";

import {useMutation, useQueryClient} from "@tanstack/react-query";

import {userKeys} from "../../user-keys";

import {deleteUserMutationOptions} from "./delete-user.mutation-options";

/**
 * Command hook: Delete a user with optimistic removal.
 *
 * DEFAULT BEHAVIOR (always applied):
 * - Optimistically remove from all list caches
 * - Rollback on error
 * - Invalidate lists on success
 *
 * USER CALLBACKS (optional, applied after defaults):
 * - onSuccess: e.g., show toast, navigate
 * - onError: e.g., show error toast
 *
 * @param callbacks - Optional callbacks for custom logic
 *
 * @example
 * const mutation = useDeleteUser({
 *   onSuccess: () => showToast("User deleted successfully"),
 *   onError: (err) => showToast(`Error: ${err.message}`),
 * });
 */
export function useDeleteUser(callbacks?: Partial<MutationCallbacks<string, void>>) {
  const queryClient = useQueryClient();
  const listFilter = {queryKey: userKeys.lists()};

  return useMutation({
    ...deleteUserMutationOptions(),

    onMutate: async (deletedId: string) => {
      // ─── DEFAULT: Optimistic removal ──────────────────────────────
      await queryClient.cancelQueries(listFilter);
      const previousLists = queryClient.getQueriesData<AppPaginatedList<User>>(listFilter);

      queryClient.setQueriesData<AppPaginatedList<User>>(listFilter, (old) => {
        if (!old) return old;

        return {
          ...old,
          items: old.items.filter((user) => user.id !== deletedId),
          meta: {
            ...old.meta,
            total: Math.max(0, old.meta.total - 1),
          },
        };
      });

      // ─── USER: Custom onMutate callback ───────────────────────────
      const userContext = await callbacks?.onMutate?.(deletedId);

      return {previousLists, userContext};
    },

    onError: (_err, deletedId, ctx) => {
      // ─── DEFAULT: Rollback ────────────────────────────────────────
      ctx?.previousLists.forEach(([key, snapshot]) => queryClient.setQueryData(key, snapshot));

      // ─── USER: Custom onError callback ───────────────────────────
      callbacks?.onError?.(_err, deletedId, ctx);
    },

    onSuccess: () => {
      // ─── DEFAULT: Invalidate lists ────────────────────────────────
      void queryClient.invalidateQueries(listFilter);

      // ─── USER: Custom onSuccess callback ──────────────────────────
      callbacks?.onSuccess?.(undefined);
    },
  });
}
